/**
 * WorkerDocuments — migrated from mockData to live svdy-platform API.
 *
 * Scope reconciled with the backend contract:
 *
 * - This page manages DOCUMENT TYPES (the category catalog — Passport,
 *   Employment Contract, NDA, etc). Per-worker file uploads are a
 *   separate concept under /v1/organization/workers/{id}/documents.
 * - Per-worker uploads need an S3 signed-URL flow that's not wired
 *   in the UI yet. A banner at the top of this page surfaces the
 *   blocker; the per-worker file table belongs on a future
 *   ActiveWorkers detail view.
 *
 * UX:
 *   - is_required → Checkbox in the form, boolean column in the table.
 *   - status active/inactive → Select in the form, Chip in the table.
 *   - files_count / workers_count are backend-computed, read-only.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { useTranslation } from 'react-i18next';
import { useDocumentViewer } from '../../../components/common/useDocumentViewer';

import {
  ApiError,
  organizationApi,
  type Schema,
} from '../../../api';
import type { DocumentTypeChangePreview } from '../../../api/organization';
import { ConfirmDialog } from '../../../components/primitives';
import DateField from '../../../components/inputs/DateField';
import { activeLocale } from '../../../utils/datetime';
import { downloadText } from '../../../utils/downloadFile';
import { translateApiError } from '../../../i18n/translateApiError';

type DocumentType = Schema<'DocumentTypeResponse'>;
type DocumentTypeCreate = Schema<'DocumentTypeCreate'>;
type DocumentTypeUpdate = Schema<'DocumentTypeUpdate'>;

type FormState = Partial<DocumentType>;

const STATUS_ACTIVE = 'active';
const STATUS_INACTIVE = 'inactive';

// ---------------------------------------------------------------------------
// Type → Workers drill-down dialog
// ---------------------------------------------------------------------------

/** Minimal RFC-4180-ish CSV parser (handles quoted fields, escaped
 *  quotes and embedded commas/newlines). The manifest endpoint emits
 *  exactly this shape from Python's csv.writer. */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (c !== '\r') {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

interface ManifestRow {
  worker_code: string;
  full_name: string;
  uploaded: boolean;
  uploaded_at: string;
  file_name: string;
  file_url: string;
}

const DocumentTypeWorkersDialog: React.FC<{
  documentType: DocumentType | null;
  onClose: () => void;
  onExportCsv: (row: DocumentType) => void;
}> = ({ documentType, onClose, onExportCsv }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<ManifestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { openDocument, viewer } = useDocumentViewer();

  useEffect(() => {
    if (!documentType) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    organizationApi.documentTypesApi
      .manifestCsv(documentType.id)
      .then((csv) => {
        if (cancelled) return;
        const parsed = parseCsv(csv as unknown as string);
        const header = parsed[0] ?? [];
        const idx = (k: string) => header.indexOf(k);
        const iName = idx('full_name');
        const iCode = idx('worker_code');
        const iUploaded = idx('uploaded');
        const iAt = idx('uploaded_at');
        const iFile = idx('file_name');
        const iUrl = idx('file_url');
        setRows(
          parsed
            .slice(1)
            .filter((r) => r.length > 1)
            .map((r) => ({
              full_name: r[iName] ?? '',
              worker_code: r[iCode] ?? '',
              uploaded: (r[iUploaded] ?? '').toLowerCase() === 'yes',
              uploaded_at: r[iAt] ?? '',
              file_name: r[iFile] ?? '',
              file_url: r[iUrl] ?? '',
            })),
        );
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? translateApiError(e, t) : t('org:docTypeDrill.loadFailed'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentType, t]);

  const uploadedCount = useMemo(() => rows.filter((r) => r.uploaded).length, [rows]);

  return (
    <>
    <Dialog open={!!documentType} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        {t('org:docTypeDrill.title', { name: documentType?.name ?? '' })}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('org:docTypeDrill.subtitle')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={26} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ my: 1 }}>
            {error}
          </Alert>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('org:docTypeDrill.empty')}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              <strong>{uploadedCount}</strong>{' '}
              {t('org:docTypeDrill.summary', { total: rows.length })}
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
              <Table size="small">
                <TableHead
                  sx={{ bgcolor: 'grey.50', '& .MuiTableCell-root': { fontWeight: 600 } }}
                >
                  <TableRow>
                    <TableCell>{t('org:docTypeDrill.colWorker')}</TableCell>
                    <TableCell>{t('org:docTypeDrill.colStatus')}</TableCell>
                    <TableCell>{t('org:docTypeDrill.colUploadedAt')}</TableCell>
                    <TableCell align="right">{t('org:docTypeDrill.colFile')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={`${r.worker_code}-${i}`} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {r.full_name || '—'}
                        </Typography>
                        {r.worker_code && (
                          <Typography variant="caption" color="text.secondary">
                            {r.worker_code}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.uploaded ? t('org:docTypeDrill.uploaded') : t('org:docTypeDrill.missing')}
                          color={r.uploaded ? 'success' : 'default'}
                          variant={r.uploaded ? 'filled' : 'outlined'}
                          sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {r.uploaded_at ? new Date(r.uploaded_at).toLocaleDateString(activeLocale()) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {r.file_url ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => openDocument(r.file_url, r.file_name)}
                            startIcon={<OpenInNewOutlinedIcon sx={{ fontSize: 14 }} />}
                            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.72rem', py: 0 }}
                          >
                            {t('org:docTypeDrill.open')}
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ py: '12px' }}>
        <Button
          onClick={() => documentType && onExportCsv(documentType)}
          color="inherit"
          startIcon={<DownloadIcon fontSize="small" />}
          sx={{ textTransform: 'none' }}
        >
          {t('org:docTypeDrill.exportCsv')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} color="inherit">
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
    {viewer}
    </>
  );
};

const WorkerDocuments: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<DocumentType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [current, setCurrent] = useState<FormState | null>(null);
  const [toDelete, setToDelete] = useState<DocumentType | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  // Type → workers drill-down dialog target. Opening it answers
  // "who has uploaded this document type, and let me open their file".
  const [drillType, setDrillType] = useState<DocumentType | null>(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [requiredFilter, setRequiredFilter] = useState<string>('all');

  // The scope picker's options. Employment type is the axis because it is the one
  // the LAW uses (ADR-044 §6.2): USCIS requires Form I-9 for every individual hired
  // for employment, while a contractor gets a W-9 and a 1099.
  const [employmentTypes, setEmploymentTypes] = useState<
    { id: string; name: string }[]
  >([]);
  /**
   * S5 / F6 — the consequence of an edit, held until the admin confirms it.
   *
   * `preview` is the server's computation (null when the only thing to warn about is
   * a rename, which nothing can compute).
   */
  const [preview, setPreview] = useState<{
    preview: DocumentTypeChangePreview | null;
    renamed: boolean;
  } | null>(null);

  const employmentTypeName = useMemo(() => {
    const map = new Map<string, string>();
    for (const et of employmentTypes) map.set(et.id, et.name);
    return map;
  }, [employmentTypes]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [data, types] = await Promise.all([
        organizationApi.documentTypesApi.list(),
        // Its own await rather than a separate effect: the scope picker is unusable
        // without it, and a form that offers an empty list of employment types looks
        // like the organization has none.
        organizationApi.employmentTypesApi.list(),
      ]);
      setRows(data);
      setEmploymentTypes(types.map((et) => ({ id: et.id, name: et.name })));
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError ? translateApiError(e, t) : t('org:workerDocuments.toast.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const summaryStats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter(
      (r) => (r.status ?? STATUS_ACTIVE) === STATUS_ACTIVE,
    ).length;
    const required = rows.filter((r) => r.is_required).length;
    const totalFiles = rows.reduce((sum, r) => sum + (r.files_count ?? 0), 0);
    return { total, active, required, totalFiles };
  }, [rows]);

  const summaryCards = useMemo(
    () => [
      {
        id: 'total',
        value: summaryStats.total,
        title: t('org:workerDocuments.stats.total'),
        description: t('org:workerDocuments.stats.totalDescription'),
        avatar: {
          icon: <FolderOutlinedIcon />,
          bgcolor: 'primary.light',
          color: 'primary.main',
        },
      },
      {
        id: 'active',
        value: summaryStats.active,
        title: t('org:workerDocuments.stats.active'),
        description: t('org:workerDocuments.stats.activeDescription'),
        avatar: {
          icon: <CheckCircleOutlineIcon />,
          bgcolor: 'success.light',
          color: 'success.main',
        },
      },
      {
        id: 'required',
        value: summaryStats.required,
        title: t('org:workerDocuments.stats.required'),
        description: t('org:workerDocuments.stats.requiredDescription'),
        avatar: {
          icon: <ErrorOutlineIcon />,
          bgcolor: 'warning.light',
          color: 'warning.dark',
        },
      },
      {
        id: 'files',
        value: summaryStats.totalFiles,
        title: t('org:workerDocuments.stats.files'),
        description: t('org:workerDocuments.stats.filesDescription'),
        avatar: {
          icon: <InsertDriveFileOutlinedIcon />,
          bgcolor: 'info.light',
          color: 'info.main',
        },
      },
    ],
    [summaryStats, t],
  );

  const filtered = useMemo(() => {
    const scoped = rows.filter((r) => {
      const status = r.status ?? STATUS_ACTIVE;
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (requiredFilter === 'yes' && !r.is_required) return false;
      if (requiredFilter === 'no' && r.is_required) return false;
      return true;
    });
    const needle = filterText.trim().toLowerCase();
    if (!needle) return scoped;
    return scoped.filter(
      (r) =>
        r.name.toLowerCase().includes(needle) ||
        (r.description ?? '').toLowerCase().includes(needle),
    );
  }, [rows, filterText, statusFilter, requiredFilter]);

  const paginated = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  // ------------------------------------------------------------------
  // CRUD
  // ------------------------------------------------------------------

  const validateForm = (): boolean => {
    if (!current) return false;
    const errs: Record<string, string> = {};
    if (!current.name?.trim()) errs.name = t('org:workerDocuments.fields.nameRequired');
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleOpenAddDialog = () => {
    setCurrent({ is_required: false, status: STATUS_ACTIVE });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleOpenEditDialog = (row: DocumentType) => {
    setCurrent({ ...row });
    setFormErrors({});
    setOpenFormDialog(true);
  };

  const handleCloseFormDialog = () => {
    setOpenFormDialog(false);
    setCurrent(null);
    setFormErrors({});
  };

  /**
   * S5 (§7.5) + F6 (§7.4) — ask before saving, when the edit has a consequence the
   * admin cannot see.
   *
   * Two of them, and they are different in kind:
   *
   *  - a SCOPE or DATE change recomputes who is overdue. Expanding the scope
   *    re-introduces the instant-debt problem §6.1 rejected, and `effective_from`
   *    does not catch it because the date was never moved — one dropdown change, N
   *    instantly overdue records. So the server computes the outcome and it goes on
   *    screen BEFORE anything is written.
   *  - a RENAME silently re-purposes every existing upload against a requirement it
   *    was never checkedingst. Nothing can be computed for that; what it needs is
   *    for the dialog to say what a rename does NOT do.
   */
  const handleSave = async () => {
    if (!validateForm() || !current) return;

    // A create has nothing to compare against, and an optional type has no
    // obligations to move.
    if (current.id && current.is_required) {
      const original = rows.find((r) => r.id === current.id);
      const scopeChanged =
        original &&
        (original.applies_to_scope !== (current.applies_to_scope ?? 'all') ||
          JSON.stringify([...(original.applies_to_employment_type_ids ?? [])].sort()) !==
            JSON.stringify([...(current.applies_to_employment_type_ids ?? [])].sort()) ||
          (original.effective_from ?? null) !== (current.effective_from ?? null) ||
          (original.grace_days ?? 30) !== (current.grace_days ?? 30));
      const renamed = original && original.name !== current.name;

      if (scopeChanged) {
        try {
          const p = await organizationApi.documentTypesApi.previewChange(current.id, {
            applies_to_scope: current.applies_to_scope ?? 'all',
            applies_to_employment_type_ids: current.applies_to_employment_type_ids ?? [],
            effective_from: current.effective_from ?? null,
            grace_days: current.grace_days ?? 30,
          });
          // Only worth interrupting for when something actually moves. An edit whose
          // preview is all zeroes gets no dialog — a confirmation that always fires
          // is one people click through without reading.
          if (p.newly_overdue > 0 || p.no_longer_overdue > 0) {
            setPreview({ preview: p, renamed: !!renamed });
            return;
          }
        } catch {
          // A failed preview must not block the edit. The consequence is unshown,
          // not unsafe — and refusing to save because a read-only computation timed
          // out would be the worse failure.
        }
      }
      if (renamed) {
        setPreview({ preview: null, renamed: true });
        return;
      }
    }
    await persist();
  };

  const persist = async () => {
    if (!current) return;
    setPreview(null);
    setSaving(true);
    try {
      if (current.id) {
        const update: DocumentTypeUpdate = {
          name: current.name ?? null,
          description: current.description ?? null,
          is_required: current.is_required ?? null,
          status: current.status ?? null,
          effective_from: current.effective_from ?? null,
          grace_days: current.grace_days ?? null,
          applies_to_scope: current.applies_to_scope ?? null,
          applies_to_employment_type_ids:
            current.applies_to_employment_type_ids ?? null,
        };
        await organizationApi.documentTypesApi.update(current.id, update);
        setToast(t('org:workerDocuments.toast.updated', { name: current.name }));
      } else {
        const create: DocumentTypeCreate = {
          name: current.name!,
          description: current.description ?? null,
          is_required: current.is_required ?? false,
          effective_from: current.effective_from ?? null,
          grace_days: current.grace_days ?? null,
          applies_to_scope: current.applies_to_scope ?? null,
          applies_to_employment_type_ids:
            current.applies_to_employment_type_ids ?? null,
        };
        await organizationApi.documentTypesApi.create(create);
        setToast(t('org:workerDocuments.toast.created', { name: create.name }));
      }
      handleCloseFormDialog();
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError ? translateApiError(e, t) : t('org:workerDocuments.toast.saveFailed');
      setFormErrors((prev) => ({ ...prev, _global: message }));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (row: DocumentType) => {
    setToDelete(row);
    setOpenDeleteDialog(true);
  };

  // P1.3: download a CSV manifest for one doc type — one row per
  // active worker × the latest upload of that type. Used by admins
  // to answer 'who hasn't submitted this' without clicking through
  // 50 worker pages. Backend endpoint streams text/csv with the
  // Content-Disposition already set; we wrap the body in a Blob
  // and trigger a synthetic <a download> click rather than relying
  // on the browser to honour the header (avoids cross-origin
  // edge cases when the API host differs from the SPA host).
  const handleDownloadManifest = async (row: DocumentType) => {
    try {
      const csv = (await organizationApi.documentTypesApi.manifestCsv(
        row.id,
      )) as unknown as string;
      const safeName = (row.name ?? 'manifest').replace(/[^\w-]+/g, '_');
      // Shared delivery helper. The local copy revoked the object URL synchronously,
      // which races the download in Safari — a fix `api/organization.ts` already had in
      // a comment that this file never received.
      downloadText(csv, `document_manifest_${safeName}.csv`);
      setToast(t('org:workerDocuments.toast.manifestDownloaded', { name: row.name }));
    } catch (e) {
      setToast(
        e instanceof ApiError ? translateApiError(e, t) : t('org:workerDocuments.toast.manifestFailed'),
      );
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setToDelete(null);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      await organizationApi.documentTypesApi.delete(toDelete.id);
      setSelectedIds((prev) => prev.filter((id) => id !== toDelete.id));
      // Dialog auto-closes on success; still clear row selection.
      handleCloseDeleteDialog();
      setToast(t('org:workerDocuments.toast.deleted', { name: toDelete.name }));
      await fetchAll();
    } catch (e) {
      const message =
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:workerDocuments.toast.deleteFailed');
      setToast(message);
      // Rethrow so ConfirmDialog keeps itself open for retry.
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      setToast(t('org:workerDocuments.toast.selectOneDelete'));
      return;
    }
    setSaving(true);
    const failures: string[] = [];
    for (const id of selectedIds) {
      try {
        await organizationApi.documentTypesApi.delete(id);
      } catch {
        failures.push(id);
      }
    }
    setSelectedIds([]);
    await fetchAll();
    setSaving(false);
    if (failures.length === 0) {
      setToast(t('org:workerDocuments.toast.bulkDeleted', { count: selectedIds.length }));
    } else {
      setToast(
        t('org:workerDocuments.toast.bulkDeletedPartial', {
          ok: selectedIds.length - failures.length,
          failed: failures.length,
        }),
      );
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFormChange = (event: any) => {
    const { name, value, type, checked } = event.target;
    setCurrent((prev) =>
      prev
        ? {
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
          }
        : null,
    );
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setCurrent((prev) => (prev ? { ...prev, [name]: value } : null));
  };

  // ------------------------------------------------------------------
  // Row selection / pagination
  // ------------------------------------------------------------------

  const handleSelectRowClick = (
    _event: React.SyntheticEvent,
    id: string,
  ) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const isSelected = (id: string) => selectedIds.includes(id);

  const handleChangePage = (_event: unknown, newPage: number) =>
    setPage(newPage);
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    setPage(0);
  };
  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedIds(event.target.checked ? filtered.map((r) => r.id) : []);
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (loading && rows.length === 0) {
    return (
      <Container
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error && rows.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={fetchAll}>
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
      }}
    >
      <Alert icon={false} severity="info" sx={{ width: '100%', mb: 2 }}>
        <AlertTitle>{t('org:workerDocuments.banner.title')}</AlertTitle>
        {t('org:workerDocuments.banner.bodyPrefix')}{' '}
        <strong>{t('org:workerDocuments.banner.bodyMid')}</strong>
        {t('org:workerDocuments.banner.bodySuffix')}
      </Alert>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: '1.125rem' }}
          >
            {t('org:workerDocuments.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:workerDocuments.subtitle')}
          </Typography>
        </Box>
        <Box>
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchAll}
              sx={{
                mr: 1,
                border: '1px solid rgba(0,0,0,0.2)',
                borderRadius: 1,
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f5f5f5' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
            size="small"
            sx={{
              mr: 1,
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
            }}
          >
            {t('org:workerDocuments.addButton')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleBulkDelete}
            size="small"
            color="error"
            disabled={selectedIds.length === 0 || saving}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontSize: '0.875rem',
              bgcolor: '#ffffff',
              '&:hover': { bgcolor: '#ffffff' },
            }}
          >
            {t('org:shared.bulkDelete')}
          </Button>
        </Box>
      </Box>

      {/* Compact stats — 13" laptop friendly */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap' }}>
        {summaryCards.flatMap((card, i) => [
          i > 0 ? <Typography key={`sep-${card.id}`} variant="caption" color="text.disabled">·</Typography> : null,
          <Typography key={card.id} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {React.cloneElement(card.avatar.icon, { sx: { fontSize: 14, color: card.avatar.color } })}
            <strong>{card.value}</strong> {card.title}
          </Typography>,
        ]).filter(Boolean)}
      </Stack>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('org:workerDocuments.filters.statusLabel')}</InputLabel>
          <Select
            value={statusFilter}
            label={t('org:workerDocuments.filters.statusLabel')}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">{t('org:workerDocuments.filters.allStatuses')}</MenuItem>
            <MenuItem value={STATUS_ACTIVE}>{t('org:workerDocuments.status.active')}</MenuItem>
            <MenuItem value={STATUS_INACTIVE}>{t('org:workerDocuments.status.inactive')}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('org:workerDocuments.filters.requiredLabel')}</InputLabel>
          <Select
            value={requiredFilter}
            label={t('org:workerDocuments.filters.requiredLabel')}
            onChange={(e) => { setRequiredFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="all">{t('org:workerDocuments.filters.all')}</MenuItem>
            <MenuItem value="yes">{t('org:workerDocuments.filters.requiredYes')}</MenuItem>
            <MenuItem value="no">{t('org:workerDocuments.filters.requiredNo')}</MenuItem>
          </Select>
        </FormControl>
        <TextField
          variant="outlined"
          label={t('org:workerDocuments.searchLabel')}
          value={filterText}
          onChange={handleFilterChange}
          size="small"
          sx={{ width: 320, maxWidth: '100%' }}
        />
        <Box sx={{ flex: 1 }} />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filtered.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }}
        />
      </Box>

      {/* Chips §5 */}
      {(() => {
        const chips: { key: string; label: string; onClear: () => void }[] = [];
        if (statusFilter !== 'all') {
          chips.push({
            key: 'status',
            label: t('org:workerDocuments.filters.statusChip', { label: statusFilter === STATUS_ACTIVE ? t('org:workerDocuments.status.active') : t('org:workerDocuments.status.inactive') }),
            onClear: () => { setStatusFilter('all'); setPage(0); },
          });
        }
        if (requiredFilter !== 'all') {
          chips.push({
            key: 'req',
            label: t('org:workerDocuments.filters.requiredChip', { label: requiredFilter === 'yes' ? t('org:workerDocuments.filters.requiredChipYes') : t('org:workerDocuments.filters.requiredChipOptional') }),
            onClear: () => { setRequiredFilter('all'); setPage(0); },
          });
        }
        if (filterText.trim()) {
          chips.push({
            key: 'search',
            label: t('org:workerDocuments.filters.searchChip', { query: filterText.trim() }),
            onClear: () => { setFilterText(''); setPage(0); },
          });
        }
        if (chips.length === 0) return null;
        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              gap: 0.75,
              mb: 1.5,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {chips.map((c) => (
              <Chip
                key={c.key}
                label={c.label}
                size="small"
                onDelete={c.onClear}
                variant="outlined"
                color="primary"
                sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }}
              />
            ))}
            <Button
              size="small"
              onClick={() => {
                setStatusFilter('all');
                setRequiredFilter('all');
                setFilterText('');
                setPage(0);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                color: 'text.secondary',
                minWidth: 0,
                px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: 'text.primary' },
              }}
            >
              {t('org:workerDocuments.filters.clearAll')}
            </Button>
          </Box>
        );
      })()}

      {filtered.length === 0 && !loading ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {filterText
              ? t('org:workerDocuments.empty.noMatches')
              : t('org:workerDocuments.empty.noResults')}
          </Typography>
          {!filterText && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleOpenAddDialog}
              sx={{ mt: 2 }}
            >
              {t('org:workerDocuments.addButton')}
            </Button>
          )}
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}
        >
          <Table sx={{ minWidth: 650 }} aria-label={t('org:workerDocuments.tableAria')}>
            <TableHead
              sx={{
                bgcolor: 'grey.100',
                '& .MuiTableCell-root': { fontWeight: 600 },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < filtered.length
                    }
                    checked={
                      filtered.length > 0 &&
                      selectedIds.length === filtered.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell>{t('org:workerDocuments.table.name')}</TableCell>
                <TableCell>{t('org:workerDocuments.table.description')}</TableCell>
                <TableCell>{t('org:workerDocuments.table.required')}</TableCell>
                <TableCell align="right">{t('org:workerDocuments.table.files')}</TableCell>
                <TableCell align="right">{t('org:workerDocuments.table.workers')}</TableCell>
                <TableCell>{t('org:workerDocuments.table.status')}</TableCell>
                <TableCell align="center">{t('org:workerDocuments.table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((r) => {
                const selected = isSelected(r.id);
                const active = (r.status ?? STATUS_ACTIVE) === STATUS_ACTIVE;
                return (
                  <TableRow
                    hover
                    key={r.id}
                    selected={selected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={(event) => handleSelectRowClick(event, r.id)}
                        inputProps={{ 'aria-label': r.name }}
                      />
                    </TableCell>
                    <TableCell>{r.name}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 320,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.description || '—'}
                    </TableCell>
                    <TableCell>{r.is_required ? t('org:workerDocuments.table.yes') : '—'}</TableCell>
                    <TableCell align="right">{r.files_count ?? 0}</TableCell>
                    <TableCell align="right">
                      {r.workers_count ?? 0}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={active ? t('org:workerDocuments.table.active') : t('org:workerDocuments.table.inactive')}
                        color={active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('org:docTypeDrill.viewTooltip')}>
                        <IconButton
                          size="small"
                          aria-label={t('org:docTypeDrill.viewTooltip')}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDrillType(r);
                          }}
                        >
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        size="small"
                        aria-label={t('org:shared.table.edit')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDialog(r);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={t('common:actions.delete')}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteDialog(r);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={openFormDialog}
        onClose={handleCloseFormDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {current?.id ? t('org:workerDocuments.editDialogTitle') : t('org:workerDocuments.addDialogTitle')}
        </DialogTitle>
        <DialogContent>
          {formErrors._global && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors._global}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label={t('org:workerDocuments.fields.name')}
            fullWidth
            required
            value={current?.name || ''}
            onChange={handleFormChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="description"
            label={t('org:workerDocuments.fields.description')}
            fullWidth
            multiline
            rows={3}
            value={current?.description || ''}
            onChange={handleFormChange}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!current?.is_required}
                onChange={handleFormChange}
                name="is_required"
                color="primary"
              />
            }
            label={t('org:workerDocuments.fields.isRequired')}
            sx={{ mb: 0.5 }}
          />
          {/* F3 — the copy fix, and the cheapest half of ADR-044. "Required" used to
              over-promise: it was enforced in exactly ONE place in the product (a
              client-side check on the Add Worker form, create mode only), so an
              admin who ticked it believed something the product did not do. This
              says what it actually means. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 2 }}
          >
            {t('org:workerDocuments.fields.isRequiredHelp')}
          </Typography>

          {/* S3's two new fields. Only shown when the type is required, because a
              due date for an optional document is a number with no meaning. */}
          {current?.is_required && (
            <>
              <Box sx={{ display: 'flex', gap: 2, mb: 0.5 }}>
                {/* `DateField`'s onChange is EVENT-shaped so the page's generic
                    `handleFormChange` can drive it — used here directly rather than
                    rewiring it, because the shape is its contract. */}
                <DateField
                  name="effective_from"
                  margin="dense"
                  label={t('org:workerDocuments.fields.effectiveFrom')}
                  value={current?.effective_from ?? ''}
                  onChange={(e) =>
                    setCurrent((prev) =>
                      prev
                        ? { ...prev, effective_from: e.target.value || null }
                        : prev,
                    )
                  }
                  fullWidth
                />
                <TextField
                  margin="dense"
                  name="grace_days"
                  label={t('org:workerDocuments.fields.graceDays')}
                  type="number"
                  fullWidth
                  value={current?.grace_days ?? 30}
                  onChange={(e) =>
                    setCurrent((prev) =>
                      prev
                        ? { ...prev, grace_days: Math.max(0, Number(e.target.value) || 0) }
                        : prev,
                    )
                  }
                  inputProps={{ min: 0 }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 2 }}
              >
                {t('org:workerDocuments.fields.datesHelp')}
              </Typography>

              <FormControl fullWidth margin="dense" sx={{ mb: 0.5 }}>
                <InputLabel id="scope-label">
                  {t('org:workerDocuments.fields.appliesTo')}
                </InputLabel>
                <Select
                  labelId="scope-label"
                  value={current?.applies_to_scope ?? 'all'}
                  label={t('org:workerDocuments.fields.appliesTo')}
                  onChange={(e) =>
                    setCurrent((prev) =>
                      prev
                        ? {
                            ...prev,
                            applies_to_scope: e.target.value as 'all' | 'employment_types',
                            // Switching back to everyone CLEARS the list rather than
                            // keeping it: a stale list on an 'all' row is §7.5's
                            // instant-debt expansion waiting for somebody to flip the
                            // scope again.
                            applies_to_employment_type_ids:
                              e.target.value === 'all'
                                ? []
                                : prev.applies_to_employment_type_ids ?? [],
                          }
                        : prev,
                    )
                  }
                >
                  <MenuItem value="all">
                    {t('org:workerDocuments.scope.all')}
                  </MenuItem>
                  <MenuItem value="employment_types">
                    {t('org:workerDocuments.scope.employmentTypes')}
                  </MenuItem>
                </Select>
              </FormControl>
              {current?.applies_to_scope === 'employment_types' && (
                <FormControl fullWidth margin="dense" sx={{ mb: 0.5 }}>
                  <InputLabel id="ety-label">
                    {t('org:workerDocuments.fields.employmentTypes')}
                  </InputLabel>
                  <Select
                    labelId="ety-label"
                    multiple
                    value={current?.applies_to_employment_type_ids ?? []}
                    label={t('org:workerDocuments.fields.employmentTypes')}
                    onChange={(e) =>
                      setCurrent((prev) =>
                        prev
                          ? {
                              ...prev,
                              applies_to_employment_type_ids:
                                typeof e.target.value === 'string'
                                  ? e.target.value.split(',')
                                  : (e.target.value as string[]),
                            }
                          : prev,
                      )
                    }
                    renderValue={(selected) =>
                      (selected as string[])
                        .map((id) => employmentTypeName.get(id) ?? id)
                        .join(', ')
                    }
                  >
                    {employmentTypes.map((et) => (
                      <MenuItem key={et.id} value={et.id}>
                        {et.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 2 }}
              >
                {t('org:workerDocuments.fields.appliesToHelp')}
              </Typography>
            </>
          )}
          <FormControl fullWidth margin="dense">
            <InputLabel id="status-label">{t('org:workerDocuments.fields.status')}</InputLabel>
            <Select
              labelId="status-label"
              name="status"
              value={current?.status ?? STATUS_ACTIVE}
              label={t('org:workerDocuments.fields.status')}
              onChange={handleSelectChange}
            >
              <MenuItem value={STATUS_ACTIVE}>{t('org:workerDocuments.status.active')}</MenuItem>
              <MenuItem value={STATUS_INACTIVE}>{t('org:workerDocuments.status.inactive')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ py: '16px' }}>
          <Button onClick={handleCloseFormDialog} color="inherit">
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={24} />
            ) : current?.id ? (
              t('org:shared.saveChanges')
            ) : (
              t('org:workerDocuments.createSubmit')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openDeleteDialog}
        tone="danger"
        title={t('org:workerDocuments.deleteTitle')}
        description={t('org:workerDocuments.deleteDescription')}
        subject={
          toDelete && (
            <Box sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>
              {toDelete.name}
            </Box>
          )
        }
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete}
        onClose={handleCloseDeleteDialog}
      />

      {/* S5 (§7.5) + F6 (§7.4). Fires only when the edit MOVES something or renames
          the type — a confirmation that always appears is one people click through
          without reading. */}
      <ConfirmDialog
        open={!!preview}
        tone={
          (preview?.preview?.newly_overdue ?? 0) > 0 ? 'warning' : 'primary'
        }
        title={t('org:workerDocuments.changeConfirm.title')}
        description={t('org:workerDocuments.changeConfirm.body')}
        subject={
          <Box sx={{ fontSize: '0.875rem' }}>
            {preview?.preview && preview.preview.newly_overdue > 0 && (
              <Box sx={{ mb: 1 }}>
                <Box sx={{ fontWeight: 600, color: 'warning.main', mb: 0.5 }}>
                  {t('org:workerDocuments.changeConfirm.nowOverdue', {
                    count: preview.preview.newly_overdue,
                  })}
                </Box>
                {/* NAMED, capped by the server. §8.1's argument applies here too: a
                    count reads fine while the three people it covers are the ones the
                    admin did not mean to include. */}
                <Box sx={{ color: 'text.secondary' }}>
                  {(preview.preview.newly_overdue_workers ?? []).join(' · ')}
                  {preview.preview.newly_overdue >
                    (preview.preview.newly_overdue_workers ?? []).length && (
                    <>
                      {' '}
                      {t('org:workerDocuments.changeConfirm.andMore', {
                        count:
                          preview.preview.newly_overdue -
                          (preview.preview.newly_overdue_workers ?? []).length,
                      })}
                    </>
                  )}
                </Box>
              </Box>
            )}
            {preview?.preview && preview.preview.no_longer_overdue > 0 && (
              // The other direction, and it is NOT reassurance. Lengthening the grace
              // or moving the effective date later forgives existing debt (§7.3), so
              // the number has to be visible or a well-meant edit silences a
              // compliance gap invisibly.
              <Box sx={{ mb: 1, color: 'text.secondary' }}>
                {t('org:workerDocuments.changeConfirm.noLongerOverdue', {
                  count: preview.preview.no_longer_overdue,
                })}
              </Box>
            )}
            {preview?.renamed && (
              <Box sx={{ color: 'text.secondary' }}>
                {t('org:workerDocuments.changeConfirm.renameWarning')}
              </Box>
            )}
          </Box>
        }
        confirmLabel={t('org:workerDocuments.changeConfirm.action')}
        confirmLoadingLabel={t('common:status.saving')}
        onConfirm={persist}
        onClose={() => setPreview(null)}
      />

      <DocumentTypeWorkersDialog
        documentType={drillType}
        onClose={() => setDrillType(null)}
        onExportCsv={handleDownloadManifest}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setToast(null)}
          sx={{ width: '100%' }}
        >
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkerDocuments;
